import type { QueryClient } from '@tanstack/query-core';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import { getUnderlyingSymbolFromInstrumentString } from 'src/lib/instrumentUtils';
import { getOptionsListEntriesQuery } from 'src/queries/options';
import { getSecurityMasterEntriesQuery } from 'src/queries/securityMaster';
import type { SymbolSearchService } from 'src/services/SymbolSearchService';
import { container } from 'src/StaticContainer';
import type { ReactBindings } from 'src/types/bindings';

// Constants
export const OPTIONS_SYMBOL_SEARCH = 'OptionsSymbolSearch';

// Invalidation
export const invalidateOptionsSymbolSearch = (queryClient: QueryClient, query?: string) => {
    return queryClient.invalidateQueries({
        queryKey: query ? [OPTIONS_SYMBOL_SEARCH, query] : [OPTIONS_SYMBOL_SEARCH],
    });
};

export class OptionsSymbolSearchServiceImpl implements SymbolSearchService {
    #securityMasterService: ReactBindings['SecurityMasterService'];
    #queryClient: QueryClient;
    #log: Logger;
    #securityMasterAccessKey: string | undefined;
    #optionsListService: ReactBindings['OptionsListService'];

    constructor(
        securityMasterService: ReactBindings['SecurityMasterService'],
        queryClient: QueryClient,
        securityMasterAccessKey: string | undefined,
        optionsListService: ReactBindings['OptionsListService'],
    ) {
        this.#securityMasterService = securityMasterService;
        this.#queryClient = queryClient;
        this.#log = container.get('Logger').getSubLogger({ name: 'services:SymbolSearchService:Options' });
        this.#securityMasterAccessKey = securityMasterAccessKey;
        this.#optionsListService = optionsListService;
    }

    async search(query: string) {
        if (TRACE) {
            this.#log.trace('search', {
                query,
                securityMasterAccessKey: this.#securityMasterAccessKey,
            });
        }

        if (!this.#securityMasterAccessKey || !query) {
            return [];
        }

        // will return all symbols that start with the query
        const matchingSymbols = await this.#queryClient.fetchQuery(
            getOptionsListEntriesQuery(query, this.#optionsListService),
        );

        // will return all unique underlying symbols from the matching symbols above
        const matchingUnderlyingSymbols = Array.from(
            new Set(matchingSymbols.map((symbol) => getUnderlyingSymbolFromInstrumentString(symbol))),
        );

        // will return all security master entries for the matching underlying symbols
        const matchingEntries = await this.#queryClient.fetchQuery(
            getSecurityMasterEntriesQuery(
                this.#securityMasterAccessKey,
                this.#securityMasterService,
                matchingUnderlyingSymbols,
            ),
        );

        return matchingSymbols.map((symbol) => {
            const underlyingSymbol = getUnderlyingSymbolFromInstrumentString(symbol);
            const entry = matchingEntries.find((entry) => entry.symbol === underlyingSymbol);
            return { symbol, info: entry };
        });
    }

    get queryKey() {
        return [OPTIONS_SYMBOL_SEARCH, this.#securityMasterAccessKey ?? ''];
    }
}
